import {
  PaymentChoiceOnCard,
  PaymentChoiceOnCardInput,
  VoucherOriginalPaymentChoice,
  VoucherOriginalPaymentChoiceInput,
} from "../components/Layouts/BookingVehicleInfoCard/PaymentChoiceOnCard.props";

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function computeApproximateDaysFromDates(
  pickUp: number | Date | string,
  dropOff: number | Date | string
): number {
  const start = pickUp instanceof Date ? pickUp : new Date(pickUp);
  const end = dropOff instanceof Date ? dropOff : new Date(dropOff);
  const startUTC = Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    start.getHours(),
    start.getMinutes()
  );
  const endUTC = Date.UTC(
    end.getFullYear(),
    end.getMonth(),
    end.getDate(),
    end.getHours(),
    end.getMinutes()
  );
  const diffHours = (endUTC - startUTC) / (1000 * 60 * 60);
  return Math.max(1, Math.ceil(diffHours / 24) || 1);
}

export function computePaymentChoiceOnCard(
  input: PaymentChoiceOnCardInput
): PaymentChoiceOnCard {
  const baseTotal = Number.isFinite(input.baseTotal) ? input.baseTotal : 0;
  const discountPct = Number.isFinite(input.payNowDis as number)
    ? (input.payNowDis as number)
    : 0;
  const surchargePct = Number.isFinite(input.payLaterSurcharge as number)
    ? (input.payLaterSurcharge as number)
    : 0;
  const days = input.rentalDays > 0 ? input.rentalDays : 1;

  const payNowTotal = round2((baseTotal * (100 - discountPct)) / 100);
  const payAtPickupTotal = round2((baseTotal * (100 + surchargePct)) / 100);
  const payNowDaily = round2(payNowTotal / days);
  const payAtPickupDaily = round2(payAtPickupTotal / days);

  let cheaperSide: "payNow" | "payAtPickup" | "equal";
  if (payNowTotal < payAtPickupTotal) cheaperSide = "payNow";
  else if (payNowTotal > payAtPickupTotal) cheaperSide = "payAtPickup";
  else cheaperSide = "equal";

  const showSavingsBadge = cheaperSide === "payNow";
  const savingsAmount = showSavingsBadge
    ? round2(payAtPickupTotal - payNowTotal)
    : 0;

  return {
    payNowDaily,
    payNowTotal,
    payAtPickupDaily,
    payAtPickupTotal,
    cheaperSide,
    savingsAmount,
    showSavingsBadge,
  };
}

export function computeVoucherOriginalPaymentChoice(
  input: VoucherOriginalPaymentChoiceInput
): VoucherOriginalPaymentChoice {
  const original = computePaymentChoiceOnCard(input);

  return {
    originalPayNowDaily: original.payNowDaily,
    originalPayNowTotal: original.payNowTotal,
    originalPayAtPickupDaily: original.payAtPickupDaily,
    originalPayAtPickupTotal: original.payAtPickupTotal,
    showOriginalPayNow: original.payNowTotal > input.current.payNowTotal,
    showOriginalPayAtPickup:
      original.payAtPickupTotal > input.current.payAtPickupTotal,
  };
}

export function buildPayNowDiscountLabel(
  showChoiceOnCard: boolean,
  discountPct: number,
  hideLabel: boolean | undefined,
  discountText: string
): string | undefined {
  if (!showChoiceOnCard || !(discountPct > 0) || hideLabel) return undefined;
  return `${discountText} ${discountPct}%`;
}

export function buildPayLaterSubLabel(
  showChoiceOnCard: boolean,
  data: ReadonlyArray<{ key: string; body: string }>,
  percentOnPayLaterAmount: number | null | undefined,
  payLaterSurchargePct: number | null | undefined
): string | undefined {
  if (!showChoiceOnCard) return undefined;

  // Single pass instead of 3 separate `.find()` scans over `data`.
  let payOnCollection = "";
  let payDepositNowLabel = "";
  let surchargeText = "";
  for (const item of data) {
    if (item.key === "payOnCollection") payOnCollection = item.body || "";
    else if (item.key === "payDepositNowLabel")
      payDepositNowLabel = item.body || "";
    else if (item.key === "SurchargeText") surchargeText = item.body || "";
  }

  const parts: string[] = [];
  // Limit payOnCollection to short strings only — long CMS copy belongs on the /pay page, not the card
  if (payOnCollection && payOnCollection.length <= 60)
    parts.push(payOnCollection);
  // Skip when percentage is 0 — avoids a meaningless "0% pay-deposit-now" line
  if (
    percentOnPayLaterAmount != null &&
    percentOnPayLaterAmount > 0 &&
    payDepositNowLabel
  )
    parts.push(`${percentOnPayLaterAmount}% ${payDepositNowLabel}`);
  // Guard surchargeText so a missing label doesn't produce a bare "40%" with no context
  if (payLaterSurchargePct != null && payLaterSurchargePct > 0 && surchargeText)
    parts.push(`${surchargeText} ${payLaterSurchargePct}%`.trim());

  return parts.length > 0 ? parts.join("\n") : undefined;
}
